import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, withKeys as _withKeys, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "tw-mb-1"
}
const _hoisted_2 = ["for"]
const _hoisted_3 = ["id", "aria-expanded", "aria-disabled", "onKeydown"]
const _hoisted_4 = { class: "tw-sticky tw-top-0 tw-bg-white tw-px-3 tw-pb-2 tw-pt-3 dark:tw-bg-black" }
const _hoisted_5 = { class: "tw-flex tw-items-center tw-gap-1 tw-rounded-lg tw-border tw-bg-white tw-px-2 focus-within:tw-border-deep-orange-600 dark:tw-bg-black" }
const _hoisted_6 = {
  key: 0,
  class: "tw-grid tw-place-items-center tw-py-4 tw-text-xs tw-text-slate-500"
}
const _hoisted_7 = ["onClick"]

import { ref, computed, watch, nextTick } from 'vue';

import TablerSearch from './Icons/tabler/TablerSearch.vue';

export type ComboSelectOption = {
  value: string;
  label: string;
};

type InternalOption = ComboSelectOption & { isCustom?: boolean };


export default /*#__PURE__*/_defineComponent({
  __name: 'StudioComboSelect',
  props: {
    id: { default: '' },
    label: { default: '' },
    placeholder: { default: 'Select or type a value' },
    options: { default: () => [] },
    value: { default: '' },
    disabled: { type: Boolean, default: false },
    integerOnly: { type: Boolean, default: false }
  },
  emits: ["change"],
  setup(__props: any, { emit }) {

const props = __props;





const isOpen = ref(false);
const searchTerm = ref('');
const triggerEl = ref<HTMLElement | null>(null);
const dropdownEl = ref<HTMLElement | null>(null);
const searchInputEl = ref<HTMLInputElement | null>(null);

const displayLabel = computed(() => props.options.find(o => o.value === props.value)?.label ?? props.value);

const displayOptions = computed((): InternalOption[] => {
  const trimmed = searchTerm.value.trim();
  const term = trimmed.toLowerCase();
  const filtered = term
    ? props.options.filter(o => o.label.toLowerCase().includes(term) || o.value.toLowerCase().includes(term))
    : props.options;

  if (term) {
    const exactMatch = props.options.find(o => o.value === trimmed);
    const isValidCustom = !exactMatch && (!props.integerOnly || /^[1-9]\d*$/.test(trimmed));
    if (isValidCustom) {
      return [{ value: trimmed, label: trimmed, isCustom: true }, ...filtered];
    }
  }
  return filtered;
});

function open() {
  if (props.disabled) {
    return;
  }
  isOpen.value = true;
}

function close() {
  isOpen.value = false;
  searchTerm.value = '';
}

function onSearchInput() {
  if (!props.integerOnly) {
    return;
  }
  searchTerm.value = searchTerm.value.replace(/[^\d]/g, '').replace(/^0+/, '');
}

function select(val: string) {
  emit('change', val);
  close();
}

function repositionDropdown() {
  const trigger = triggerEl.value;
  const dropdown = dropdownEl.value;
  if (!trigger || !dropdown) {
    return;
  }
  const rect = trigger.getBoundingClientRect();
  const gap = 4;
  dropdown.style.width = `${trigger.offsetWidth}px`;
  dropdown.style.left = `${rect.left}px`;
  const spaceBelow = window.innerHeight - rect.bottom;
  dropdown.style.top =
    spaceBelow > dropdown.offsetHeight ? `${rect.bottom + gap}px` : `${rect.top - dropdown.offsetHeight - gap}px`;
}

watch(isOpen, opened => {
  if (opened) {
    nextTick(() => {
      // requestAnimationFrame ensures the dropdown is fully laid out before
      // reading offsetHeight for above/below placement decision
      requestAnimationFrame(repositionDropdown);
      searchInputEl.value?.focus();
    });
  }
});

return (_ctx: any,_cache: any) => {
  const _directive_studio_click_outside = _resolveDirective("studio-click-outside")!

  return (_openBlock(), _createElementBlock("div", null, [
    (_ctx.label)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("label", {
            for: _ctx.id || undefined,
            class: "tw-block tw-text-xs dark:tw-text-white"
          }, _toDisplayString(_ctx.label), 9, _hoisted_2)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      id: _ctx.id,
      ref_key: "triggerEl",
      ref: triggerEl,
      role: "combobox",
      "aria-expanded": isOpen.value,
      "aria-disabled": _ctx.disabled,
      tabindex: "0",
      class: _normalizeClass(["studio-combo-select tw-mt-1 tw-flex tw-w-full tw-items-center tw-rounded-md tw-border tw-bg-white tw-px-3 tw-py-2 tw-text-sm dark:tw-bg-black", {
        'tw-cursor-pointer hover:tw-border-deep-orange-500': !isOpen.value && !_ctx.disabled,
        'tw-cursor-not-allowed tw-opacity-50': _ctx.disabled,
        'tw-border-transparent tw-ring-2 tw-ring-deep-orange-500': isOpen.value
      }]),
      onClick: open,
      onKeydown: _withKeys(_withModifiers(open, ["prevent"]), ["enter","space"])
    }, [
      _createElementVNode("span", {
        class: _normalizeClass({ 'tw-text-slate-400 dark:tw-text-slate-500': !displayLabel.value })
      }, _toDisplayString(displayLabel.value || _ctx.placeholder), 3)
    ], 42, _hoisted_3),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? _withDirectives((_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "dropdownEl",
            ref: dropdownEl,
            class: "tw-fixed tw-z-50 tw-flex tw-max-h-[300px] tw-min-w-[220px] tw-flex-col tw-overflow-y-auto tw-rounded tw-border tw-border-slate-100 tw-bg-white tw-pb-1 tw-shadow-sm dark:tw-border-slate-700 dark:tw-bg-black"
          }, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(TablerSearch, { class: "tw-h-4 tw-w-4 tw-flex-none tw-text-gray-400" }),
                _withDirectives(_createElementVNode("input", {
                  ref_key: "searchInputEl",
                  ref: searchInputEl,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
                  type: "text",
                  placeholder: "Search or type custom value",
                  class: "tw-h-8 tw-w-full tw-border-none tw-bg-transparent tw-text-sm focus:tw-outline-none focus:tw-ring-0 dark:tw-text-white",
                  onInput: onSearchInput
                }, null, 544), [
                  [_vModelText, searchTerm.value]
                ])
              ])
            ]),
            (displayOptions.value.length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, " No options found "))
              : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(displayOptions.value, (opt) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: opt.isCustom ? `__custom__${opt.value}` : opt.value,
                    class: _normalizeClass(["tw-cursor-pointer tw-py-2 tw-pl-4 tw-pr-3 tw-text-xs hover:tw-bg-slate-100 hover:tw-text-deep-orange-500", {
              'tw-bg-slate-100 tw-text-deep-orange-500 dark:tw-bg-slate-900': !opt.isCustom && opt.value === _ctx.value
            }]),
                    onClick: ($event: any) => (select(opt.value))
                  }, [
                    (opt.isCustom)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode("Use \"" + _toDisplayString(opt.value) + "\"", 1)
                        ], 64))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createTextVNode(_toDisplayString(opt.label), 1)
                        ], 64))
                  ], 10, _hoisted_7))
                }), 128))
          ])), [
            [_directive_studio_click_outside, close]
          ])
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})